import { useState, useCallback, memo, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { LeadCard, Lead, LeadStatus } from "@/components/lead-card";
import { Button } from "@/components/ui/button";
import { Plus, MoreHorizontal, Trash2, UserPlus, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useHideDeactivatedUsers } from "@/hooks/use-hide-deactivated-users";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Define Kanban columns using the Lead statuses
const KANBAN_COLUMNS = [
  { id: "old-contact", title: "Altkontakt", color: "bg-gray-500" },
  { id: "new", title: "Neu", color: "bg-purple-500" },
  { id: "not-reached", title: "Nicht erreicht", color: "bg-blue-500" },
  { id: "follow-up", title: "Wiedervorlage", color: "bg-amber-400" },
  { id: "scheduled", title: "Terminiert", color: "bg-indigo-500" },
  { id: "in-progress", title: "In Prozess", color: "bg-orange-500" },
  { id: "won", title: "Gewonnen", color: "bg-green-500" },
  { id: "lost", title: "Verloren", color: "bg-red-500" },
  { id: "wrong-data", title: "Falsche Daten", color: "bg-yellow-500" },
  { id: "delete-data", title: "Daten Löschen", color: "bg-red-700" }
] as const;

// Interface für Spalten
interface KanbanColumn {
  id: LeadStatus;
  title: string;
  color: string;
}

interface KanbanBoardProps {
  leads: Lead[];
  onLeadClick?: (lead: Lead) => void;
  onLeadStatusChange?: (leadId: string, newStatus: LeadStatus) => void;
  onNewLeadInColumn?: (status: LeadStatus) => void;
  onLeadDelete?: (leadId: string) => void;
  onLeadAssign?: (leadId: string, userId: string) => void;
  onLeadTakeover?: (leadId: string) => void;
  users?: Array<{ id: string; name: string; email: string; role: string; active?: boolean; }>;
}

// Memoize der Column Header Komponente
const ColumnHeader = memo(({ 
  column, 
  totalLeads,
  columnLeads,
  onNewLeadInColumn,
  onLeadDelete,
  onLeadAssign,
  onLeadTakeover,
  users = []
}: {
  column: KanbanColumn,
  totalLeads: number,
  columnLeads: Lead[],
  onNewLeadInColumn?: (status: LeadStatus) => void,
  onLeadDelete?: (leadId: string) => void,
  onLeadAssign?: (leadId: string, userId: string) => void,
  onLeadTakeover?: (leadId: string) => void,
  users?: Array<{ id: string; name: string; email: string; role: string; active?: boolean; }>
}) => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [hideDeactivated] = useHideDeactivatedUsers();
  const visibleUsers = hideDeactivated ? users.filter(u => u.active !== false) : users;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);

  const handleNewLeadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onNewLeadInColumn) {
      onNewLeadInColumn(column.id as LeadStatus);
    }
  };

  const handleDeleteAllLeads = () => {
    if (onLeadDelete) {
      // Get all lead IDs in this column
      columnLeads.forEach(lead => onLeadDelete(lead.id));
      toast({
        title: "Leads gelöscht",
        description: `${columnLeads.length} Leads wurden gelöscht`,
      });
    }
    setIsDeleteDialogOpen(false);
  };

  const handleTakeoverAllLeads = () => {
    if (onLeadTakeover && currentUser) {
      // Get all lead IDs in this column
      columnLeads.forEach(lead => onLeadTakeover(lead.id));
      toast({
        title: "Leads übernommen",
        description: `${columnLeads.length} Leads wurden Ihnen zugewiesen`,
      });
    }
  };

  const handleAssignAllLeads = (userId: string) => {
    if (onLeadAssign) {
      // Get all lead IDs in this column
      columnLeads.forEach(lead => onLeadAssign(lead.id, userId));
      toast({
        title: "Leads zugewiesen",
        description: `${columnLeads.length} Leads wurden einem anderen Benutzer zugewiesen`,
      });
    }
    setIsAssignDialogOpen(false);
  };

  return (
    <div className="p-3 border-b">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center">
          <div className={`w-2 h-2 ${column.color} rounded-full mr-2`}></div>
          <h3 className="text-sm font-medium text-gray-900">{column.title}</h3>
          <div className="ml-2 bg-gray-100 text-gray-600 text-xs py-0.5 px-1.5 rounded-full">
            {totalLeads}
          </div>
        </div>
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 rounded-md text-gray-500 hover:text-gray-700"
            onClick={handleNewLeadClick}
          >
            <Plus size={16} />
          </Button>
          {currentUser?.role === "admin" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7 rounded-md text-gray-500 hover:text-gray-700"
                >
                  <MoreHorizontal size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Spalten-Aktionen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleTakeoverAllLeads}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Alle übernehmen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsAssignDialogOpen(true)}>
                  <User className="h-4 w-4 mr-2" />
                  Alle zuweisen an...
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Alle löschen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      
      {/* <Progress 
        value={totalLeads > 0 ? 100 : 0} 
        className="h-1.5 bg-gray-100" 
      /> */}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alle Leads löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie wirklich alle Leads in dieser Spalte löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAllLeads}
              className="bg-red-600 hover:bg-red-700"
            >
              Alle löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign to User Dialog */}
      <AlertDialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alle Leads zuweisen</AlertDialogTitle>
            <AlertDialogDescription>
              Wählen Sie einen Benutzer aus, dem alle Leads in dieser Spalte zugewiesen werden sollen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <div className="max-h-[200px] overflow-y-auto space-y-2">
              {visibleUsers.map((user) => (
                <Button
                  key={user.id}
                  variant="ghost"
                  className={`w-full justify-start ${!user.active ? "text-muted-foreground opacity-60" : ""}`}
                  onClick={() => handleAssignAllLeads(user.id)}
                >
                  <User className="h-4 w-4 mr-2" />
                  {user.name}
                </Button>
              ))}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});

ColumnHeader.displayName = "ColumnHeader";

// Memoize der Lead Item Komponente
const LeadItem = memo(({ 
  lead, 
  index, 
  onClick,
  onLeadDelete,
  onLeadAssign,
  onLeadTakeover,
  users = []
}: { 
  lead: Lead, 
  index: number, 
  onClick?: (lead: Lead) => void,
  onLeadDelete?: (leadId: string) => void,
  onLeadAssign?: (leadId: string, userId: string) => void,
  onLeadTakeover?: (leadId: string) => void,
  users?: Array<{ id: string; name: string; email: string; role: string; }>
}) => {
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(lead);
    }
  }, [lead, onClick]);

  return (
    <Draggable key={lead.id} draggableId={lead.id} index={index}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className="mb-2"
          onClick={handleClick}
        >
          <LeadCard 
            lead={lead}
            onLeadDelete={onLeadDelete}
            onLeadAssign={onLeadAssign}
            onLeadTakeover={onLeadTakeover}
            users={users}
          />
        </div>
      )}
    </Draggable>
  );
});

LeadItem.displayName = "LeadItem";

// Improved Column Content component with enhanced dropzone behavior
const ColumnContent = memo(({ 
  columnId, 
  columnLeads, 
  onLeadClick,
  onLeadDelete,
  onLeadAssign,
  onLeadTakeover,
  users = []
}: { 
  columnId: string, 
  columnLeads: Lead[], 
  onLeadClick?: (lead: Lead) => void,
  onLeadDelete?: (leadId: string) => void,
  onLeadAssign?: (leadId: string, userId: string) => void,
  onLeadTakeover?: (leadId: string) => void,
  users?: Array<{ id: string; name: string; email: string; role: string; }>
}) => {
  
  return (
    <Droppable 
      droppableId={columnId}
      type="COLUMN"
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`p-2 min-h-[200px] overflow-y-auto relative ${
            snapshot.isDraggingOver ? "bg-blue-50" : ""
          }`}
          style={{
            transition: "background 0.2s ease, border 0.2s ease",
            border: snapshot.isDraggingOver 
              ? "2px dashed rgba(59, 130, 246, 0.5)" 
              : "2px solid transparent",
            borderRadius: "8px",
            minHeight: "100%",
            height: "100%",
          }}
        >
          {columnLeads.map((lead, index) => (
            <LeadItem 
              key={lead.id} 
              lead={lead} 
              index={index} 
              onClick={onLeadClick}
              onLeadDelete={onLeadDelete}
              onLeadAssign={onLeadAssign}
              onLeadTakeover={onLeadTakeover}
              users={users}
            />
          ))}
          
          {provided.placeholder}
          
          {columnLeads.length === 0 && !snapshot.isDraggingOver && (
            <div className="absolute inset-0 flex items-center justify-center opacity-50">
              <p className="text-sm text-gray-500">Keine Einträge</p>
            </div>
          )}
        </div>
      )}
    </Droppable>
  );
});

ColumnContent.displayName = "ColumnContent";

export function KanbanBoard({ 
  leads, 
  onLeadClick, 
  onLeadStatusChange, 
  onNewLeadInColumn,
  onLeadDelete,
  onLeadAssign,
  onLeadTakeover,
  users = []
}: KanbanBoardProps) {
  const { toast } = useToast();
  
  // Use useMemo to calculate leads by status only when leads array changes
  const leadsByStatus = useMemo(() => {
    return KANBAN_COLUMNS.reduce<Record<LeadStatus, Lead[]>>((acc, column) => {
      acc[column.id as LeadStatus] = leads.filter(lead => lead.status === column.id);
      return acc;
    }, {} as Record<LeadStatus, Lead[]>);
  }, [leads]);

  // Improved drag end handler with focus on column changes rather than position
  const handleDragEnd = useCallback((result: any) => {
    const { destination, source, draggableId } = result;

    // Dropped outside a droppable area
    if (!destination) return;

    // If dropped in same column and same position, do nothing
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // If the column changed, update the lead status regardless of position within column
    if (destination.droppableId !== source.droppableId) {
      const leadId = draggableId;
      const newStatus = destination.droppableId as LeadStatus;

      // Call callback to update status
      if (onLeadStatusChange) {
        onLeadStatusChange(leadId, newStatus);
        
        // Find column title for the toast message
        const columnTitle = KANBAN_COLUMNS.find(c => c.id === newStatus)?.title || "";
        toast({
          title: "Lead Status geändert",
          description: `Lead wurde nach "${columnTitle}" verschoben`,
        });
      }
    }
  }, [onLeadStatusChange, toast]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-6 overflow-x-auto h-full">
        {KANBAN_COLUMNS.map(column => {
          const columnLeads = leadsByStatus[column.id] || [];
          const totalLeads = columnLeads.length;
          
          return (
            <div key={column.id} className="flex-shrink-0 w-72">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
                <ColumnHeader 
                  column={column} 
                  totalLeads={totalLeads}
                  columnLeads={columnLeads}
                  onNewLeadInColumn={onNewLeadInColumn}
                  onLeadDelete={onLeadDelete}
                  onLeadAssign={onLeadAssign}
                  onLeadTakeover={onLeadTakeover}
                  users={users}
                />
                <div className="flex-1 flex flex-col h-full min-h-[300px]">
                  <ColumnContent 
                    columnId={column.id} 
                    columnLeads={columnLeads} 
                    onLeadClick={onLeadClick}
                    onLeadDelete={onLeadDelete}
                    onLeadAssign={onLeadAssign}
                    onLeadTakeover={onLeadTakeover}
                    users={users}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
